import { useEffect, useRef } from "react";
import { Box } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

import {
  SHELL_PHONE_BROWSER_MATCH,
  SHELL_PHONE_BROWSER_MEDIA,
  SHELL_PHONE_SCROLL_TO_PX,
  SHELL_TOP_BAR_PX,
} from "config";

const PULL_TRIGGER_PX = 72;
const PULL_MAX_PX = 110;
const INDICATOR_PX = 40;

const isTyping = () => {
  const el = document.activeElement as HTMLElement | null;
  return (
    !!el &&
    (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))
  );
};

/** Whether a scroll area under the touch can still move the finger's way. */
const innerCanScroll = (target: EventTarget | null, fingerDown: boolean) => {
  for (
    let node = target instanceof Element ? target : null;
    node && node !== document.body;
    node = node.parentElement
  ) {
    const { overflowY } = window.getComputedStyle(node);
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      node.scrollHeight > node.clientHeight + 1 &&
      (fingerDown
        ? node.scrollTop > 0
        : node.scrollTop + node.clientHeight < node.scrollHeight - 1)
    ) {
      return true;
    }
  }
  return false;
};

/**
 * Phone Safari tab only. Safari 26 paints the page under its status bar and
 * toolbar only while the document is scrolled, so the document is held at a
 * fixed offset and touches never move it: the shell scrolls inside. Safari's
 * own pull to refresh needs the document to move, so a pull down from the top
 * is handled here instead.
 */
export default function PhoneSafariShellHold() {
  const indicatorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const phone = window.matchMedia(SHELL_PHONE_BROWSER_MATCH);
    const hold = () => {
      if (
        phone.matches &&
        !isTyping() &&
        window.scrollY !== SHELL_PHONE_SCROLL_TO_PX
      ) {
        window.scrollTo(0, SHELL_PHONE_SCROLL_TO_PX);
      }
    };
    const showPull = (px: number, animate: boolean) => {
      const el = indicatorRef.current;
      if (!el) return;
      el.style.transition = animate
        ? "transform 200ms ease, opacity 200ms ease"
        : "none";
      el.style.opacity = String(Math.min(1, px / PULL_TRIGGER_PX));
      el.style.transform = `translate(-50%, ${px - INDICATOR_PX}px) rotate(${px * 4}deg)`;
    };

    let lastX = 0;
    let lastY = 0;
    let pull = 0;
    let pulling = false;
    let frame = 0;

    const onStart = (e: TouchEvent) => {
      lastX = e.touches[0].clientX;
      lastY = e.touches[0].clientY;
      pull = 0;
      pulling = false;
    };
    const onMove = (e: TouchEvent) => {
      if (!phone.matches || e.touches.length !== 1) return;
      const dx = e.touches[0].clientX - lastX;
      const dy = e.touches[0].clientY - lastY;
      lastX = e.touches[0].clientX;
      lastY = e.touches[0].clientY;
      if (!pulling) {
        if (Math.abs(dx) > Math.abs(dy) || dy === 0) return;
        if (innerCanScroll(e.target, dy > 0)) return;
        if (e.cancelable) e.preventDefault();
        if (dy < 0) return;
        pulling = true;
      }
      if (e.cancelable) e.preventDefault();
      pull = Math.max(0, Math.min(PULL_MAX_PX, pull + dy * 0.5));
      showPull(pull, false);
      if (pull === 0) pulling = false;
    };
    const onEnd = (e: TouchEvent) => {
      if (e.touches.length || !pulling) return;
      pulling = false;
      if (pull >= PULL_TRIGGER_PX) {
        showPull(PULL_TRIGGER_PX, true);
        window.location.reload();
      } else {
        showPull(0, true);
      }
      pull = 0;
    };
    // Only code moves the document now (focus, a menu opening): put it back.
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        hold();
      });
    };

    hold();
    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onEnd, { passive: true });
    document.addEventListener("touchcancel", onEnd, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    phone.addEventListener("change", hold);
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
      window.removeEventListener("scroll", onScroll);
      phone.removeEventListener("change", hold);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <Box
      ref={indicatorRef}
      aria-hidden
      sx={{
        display: "none",
        [SHELL_PHONE_BROWSER_MEDIA]: { display: "flex" },
        position: "absolute",
        left: "50%",
        top: SHELL_TOP_BAR_PX,
        zIndex: (theme) => theme.zIndex.appBar - 1,
        width: INDICATOR_PX,
        height: INDICATOR_PX,
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
        bgcolor: "background.paper",
        color: "primary.main",
        boxShadow: 3,
        opacity: 0,
        transform: `translate(-50%, -${INDICATOR_PX}px)`,
        pointerEvents: "none",
      }}
    >
      <RefreshIcon fontSize="small" />
    </Box>
  );
}
